import React, { useEffect, useContext, useLayoutEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import useUserDetails from "../hooks/useUserDetails";
import { Helmet } from "react-helmet";
import Modal from "../components/Modal";
import InfoBoxItem from "../components/InfoBoxItem";

import axiosInstance, { clearUserDetailStorage } from "../api/AxiosConfig";

import styles from "../css/ShopOption.module.css";

import { updateCampaign } from "../utils/axios_functions/updateCampaign";
import { getDateWithNextMonth } from "../utils/functions/getDateWithNextMonth";
import { useTranslation } from "react-i18next";

import { FaUsersGear } from "react-icons/fa6";

const ShopOption = () => {
  // Status možnosti pro dropdown
  const statusOptions = [
    "aktivní",
    "pozastaveno",
    "neaktivní",
    "registrace",
    "ukončeno",
  ];

  // Operátory pro pokročilé dotazy
  const queryOperators = [">=", "<=", "!=", "==", ">", "<"];

  const navigate = useNavigate();
  const { lng } = useParams();
  const { t } = useTranslation();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showReactivateConfirmation, setShowReactivateConfirmation] =
    useState(false);
  const [reactivateCampaignToo, setReactivateCampaignToo] = useState(false);
  // Přidána možnost "Ukončeno"
  const [selectedStatuses, setSelectedStatuses] = useState({
    Aktivní: true,
    Pozastaveno: true,
    Neaktivní: true,
    Registrace: true,
    Ukončeno: true,
  });
  // Nový stav pro výběr řazení – "name" nebo "status"
  const [sortOption, setSortOption] = useState("name");
  // Stav pro vyhledávací řetězec
  const [searchQuery, setSearchQuery] = useState("");

  const {
    checkLoginStatus,
    userData,
    setUserData,
    uuid,
    setUuid,
    appContentType,
    selectedShop,
    setSelectedShop,
    isAdmin,
    setIsUserDataLoading,
    showAdminModal,
    setShowAdminModal,
    infoItems,
  } = useContext(AppContext);

  useLayoutEffect(() => {
    setIsUserDataLoading(true);
  }, [setIsUserDataLoading]);

  useEffect(() => {
    checkLoginStatus();
  }, [checkLoginStatus]);

  useEffect(() => {
    if (Object.keys(userData).length > 0) {
      setUuid(userData.shops[0].uuid);
      sessionStorage.setItem("x23uU09I098D", userData.shops[0].uuid);
      setSelectedShop(userData?.shops[0]);
    }
  }, [userData, setSelectedShop, setUuid]);

  useUserDetails();

  // Nové funkce pro pokročilé dotazy
  const evaluateComplexQuery = (shop, query) => {
    // Nejdřív zpracuj OR operátory (mají nižší prioritu)
    if (query.includes("|")) {
      const orParts = query.split("|").map((part) => part.trim());
      return orParts.some((part) => evaluateComplexQuery(shop, part));
    }

    // Pak zpracuj AND operátory
    if (query.includes("&")) {
      const andParts = query.split("&").map((part) => part.trim());
      return andParts.every((part) => evaluateComplexQuery(shop, part));
    }

    // Pokud obsahuje s: prefix, vyhodnoť podle statusu
    if (query.startsWith("s:")) {
      const statusValue = query.replace("s:", "").trim();
      const baseStatus = getShopStatus(shop);
      const shopEndDate = shop.end_date ? new Date(shop.end_date) : null;
      const status = shopEndDate ? "Ukončeno" : baseStatus;
      return status.toLowerCase() === statusValue;
    }

    // Jinak zpracuj jako jednoduchý dotaz s operátory
    return evaluateSimpleQuery(shop, query);
  };

  const evaluateSimpleQuery = (shop, query) => {
    // Najít operátor v dotazu
    const operator = queryOperators.find((op) => query.includes(op));
    if (!operator) return false;

    const [field, valueStr] = query.split(operator).map((s) => s.trim());

    // Získat hodnotu z obchodu
    const shopValue = getShopFieldValue(shop, field.toLowerCase());
    if (shopValue === null || shopValue === undefined) return false;

    // Převést hodnotu na číslo pokud je to možné
    const numericShopValue = parseFloat(shopValue);
    const numericQueryValue = parseFloat(valueStr);

    // Pokud obě hodnoty jsou čísla, porovnávej jako čísla
    if (!isNaN(numericShopValue) && !isNaN(numericQueryValue)) {
      switch (operator) {
        case ">":
          return numericShopValue > numericQueryValue;
        case "<":
          return numericShopValue < numericQueryValue;
        case ">=":
          return numericShopValue >= numericQueryValue;
        case "<=":
          return numericShopValue <= numericQueryValue;
        case "==":
          return numericShopValue === numericQueryValue;
        case "!=":
          return numericShopValue !== numericQueryValue;
        default:
          return false;
      }
    } else {
      // Textové porovnání
      const shopStr = String(shopValue).toLowerCase();
      const queryStr = String(valueStr).toLowerCase();

      switch (operator) {
        case "==":
          return shopStr === queryStr;
        case "!=":
          return shopStr !== queryStr;
        case ">":
          return shopStr > queryStr;
        case "<":
          return shopStr < queryStr;
        case ">=":
          return shopStr >= queryStr;
        case "<=":
          return shopStr <= queryStr;
        default:
          return false;
      }
    }
  };

  const getShopFieldValue = (shop, fieldName) => {
    const fieldMap = {
      // Základní informace
      uuid: shop.uuid,
      website: shop.website,
      name: shop.name,
      ico: shop.ico,
      dic: shop.dic,
      feed: shop.feed,
      country: shop.country,
      city: shop.city,
      postal_code: shop.postal_code,
      street: shop.street,
      email: shop.email,
      phone: shop.phone,
      first_name: shop.first_name,
      last_name: shop.last_name,
      country_billing: shop.country_billing,
      email_billing: shop.email_billing,
      description: shop.description,

      // Datumy
      start_date: shop.start_date,
      end_date: shop.end_date,
      first_stat_date: shop.first_stat_date,
      registration_completion_date: shop.registration_completion_date,
      trial_end_date: shop.trial_end_date,

      // Finanční údaje
      budget: shop.budget,
      currency: shop.currency,
      pno: shop.pno,

      // Statusy
      status_create: shop.status_create,
      google_ads_status: shop.google_ads_status,
      google_merchant_status: shop.google_merchant_status,

      // Google služby
      google_merchant: shop.google_merchant,
      google_analytics: shop.google_analytics,
      google_ads: shop.google_ads,

      // Pro vyhledávání v users emailech
      users_emails: shop.users
        ? shop.users.map((user) => user.email).join(" ")
        : "",

      // Pro billing_info (pokud potřebujete hledat v příjmech/fakturaci)
      revenue:
        shop.billing_info && shop.billing_info.length > 0
          ? shop.billing_info[0].revenue
          : 0,
      fakturace:
        shop.billing_info && shop.billing_info.length > 0
          ? shop.billing_info[0].fakturace
          : 0,
    };

    return fieldMap[fieldName];
  };

  // Funkce pro detekci pokročilých dotazů
  const hasAdvancedQuery = (query) => {
    return (
      queryOperators.some((op) => query.includes(op)) ||
      query.includes("&") ||
      query.includes("|") ||
      (query.includes("s:") && (query.includes("&") || query.includes("|")))
    );
  };

  const handleShopSelect = (selectedShopUuid) => {
    setUuid(selectedShopUuid);
    setSelectedShop(
      userData?.shops.find((oneShop) => oneShop.uuid === selectedShopUuid)
    );
    sessionStorage.setItem("x23uU09I098D", selectedShopUuid);
  };

  const handleSubmit = () => {
    if (selectedShop.status_create === "end") {
      window.scrollTo(0, 0);
      navigate(`/${lng}/app/${appContentType}/`);
    } else {
      window.scrollTo(0, 0);
      navigate(`/${lng}/app/register-shop/`);
    }
  };

  const handleNew = () => {
    sessionStorage.removeItem("x23uU09I098D");
    setSelectedShop("");
    setUuid("");
    window.scrollTo(0, 0);
    navigate(`/${lng}/app/register-shop/`);
  };

  const removeProtocol = (url) => {
    return url.replace(/^https?:\/\/(www\.)?/, "");
  };

  const onTurnOffShopClick = () => {
    setShowAdminModal(false);
    setShowDeleteConfirmation(true);
  };

  // Dohledá hlavní (PMax) kampaň obchodu – stejná logika jako getShopStatus.
  // Kampaň se identifikuje svým campaign_id, obchod na ni nemá přímý odkaz.
  const getMainCampaign = (oneShop) =>
    oneShop?.campaign?.find((oneCampaign) =>
      oneCampaign.campaign_type?.toLowerCase().includes("pmax")
    );

  const handleTurnOffShop = async () => {
    try {
      setIsUserDataLoading(true);
      // Pozastavíme hlavní kampaň podle jejího campaign_id (pokud existuje).
      const mainCampaign = getMainCampaign(selectedShop);
      if (mainCampaign?.campaign_id) {
        await updateCampaign(mainCampaign.campaign_id, {
          campaign_status: "paused",
        });
      }
      await axiosInstance.patch(`shop/update/${selectedShop.uuid}/`, {
        end_date: getDateWithNextMonth(),
      });
      const response = await axiosInstance.get(`user/detail/`);
      setUserData(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsUserDataLoading(false);
    }
  };

  // Zjistí, zda je hlavní kampaň obchodu pozastavená (paused) –
  // podle toho se v potvrzovacím modalu nabídne volba znovu spustit kampaň.
  const hasPausedCampaign = (oneShop) =>
    getMainCampaign(oneShop)?.campaign_status === "paused";

  const onReactivateShopClick = () => {
    setShowAdminModal(false);
    // Předvyplníme checkbox podle toho, jestli je kampaň pozastavená.
    setReactivateCampaignToo(hasPausedCampaign(selectedShop));
    setShowReactivateConfirmation(true);
  };

  const handleReactivateShop = async () => {
    try {
      setIsUserDataLoading(true);
      // Reaktivace = zrušení end_date (obchod přestane být "Ukončeno").
      await axiosInstance.patch(`shop/update/${selectedShop.uuid}/`, {
        end_date: null,
      });
      // Volitelně znovu spustíme pozastavenou kampaň (podle jejího campaign_id).
      const mainCampaign = getMainCampaign(selectedShop);
      if (reactivateCampaignToo && mainCampaign?.campaign_id) {
        await updateCampaign(mainCampaign.campaign_id, {
          campaign_status: "active",
        });
      }
      const response = await axiosInstance.get(`user/detail/`);
      setUserData(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsUserDataLoading(false);
    }
  };

  // Pomocná funkce pro získání statusu obchodu
  const getShopStatus = (oneShop) => {
    if (oneShop.status_create !== "end") return "Registrace";
    const pmaxCampaign = oneShop?.campaign.find((oneCampaign) =>
      oneCampaign.campaign_type.toLowerCase().includes("pmax")
    );
    if (pmaxCampaign) {
      if (pmaxCampaign.campaign_status === "active") return "Aktivní";
      else if (pmaxCampaign.campaign_status === "paused") return "Pozastaveno";
      else return "Neaktivní";
    }
    return "Neaktivní";
  };

  // Handler pro změnu checkboxů
  const handleStatusChange = (e) => {
    const { value, checked } = e.target;
    setSelectedStatuses((prev) => ({
      ...prev,
      [value]: checked,
    }));
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Výběr obchodu - PermoniQ</title>
      </Helmet>
      <section className="section choose-shop">
        <article className="container" style={{ maxWidth: "50%" }}>
          <form
            className="form"
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <div className="box">
              <div
                className="select-container"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                {userData?.email === "info@permoniq.com" ? (
                  <div className={styles.headingBox}>
                    <h2>{t("shopOption.heading")}</h2>
                    <FaUsersGear
                      className={styles.usersBtn}
                      onClick={() => {
                        navigate(`/${lng}/app/users/`);
                      }}
                    />
                  </div>
                ) : (
                  <h2>{t("shopOption.heading")}</h2>
                )}
                {userData?.email === "info@permoniq.com" && (
                  <div
                    className={styles.sortAndSearchContainer}
                    style={{ position: "relative" }}
                  >
                    <input
                      type="text"
                      className={styles.searchInput}
                      placeholder="Vyhledávání..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                      }}
                      autoComplete="off"
                    />

                    <select
                      className={styles.sortSelect}
                      id="sortSelect"
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value)}
                    >
                      <option value="name">{t("shopOption.valueName")}</option>
                      <option value="status">
                        {t("shopOption.valueState")}
                      </option>
                      <option value="start_date">
                        {t("shopOption.valueDate")}
                      </option>
                    </select>
                  </div>
                )}
                <ol
                  className="shop-list"
                  style={{
                    width: "90%",
                    listStyleType: "none",
                    padding: 0,
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  {Object.keys(userData).length > 0 &&
                    userData.shops
                      .filter((oneShop) => {
                        // Filtrování podle vyhledávacího řetězce
                        if (searchQuery) {
                          const query = searchQuery.toLowerCase();

                          // Pokud obsahuje pokročilé operátory nebo kombinace, vyhodnoť jako komplexní dotaz
                          if (hasAdvancedQuery(query)) {
                            return evaluateComplexQuery(oneShop, query);
                          }
                          // Pokud začíná jen na 's:' (bez dalších operátorů), filtruj podle statusu
                          else if (query.startsWith("s:")) {
                            const statusValue = query.replace("s:", "").trim();
                            const baseStatus = getShopStatus(oneShop);
                            const shopEndDate = oneShop.end_date
                              ? new Date(oneShop.end_date)
                              : null;
                            const status = shopEndDate
                              ? "Ukončeno"
                              : baseStatus;
                            if (status.toLowerCase() !== statusValue) {
                              return false;
                            }
                          } else {
                            const website = removeProtocol(
                              oneShop.website
                            ).toLowerCase();

                            // 1. Zkontrolujte top-level vlastnosti obchodu
                            const shopNameMatch =
                              oneShop.name &&
                              oneShop.name.toLowerCase().includes(query);
                            const shopFirstNameMatch =
                              oneShop.first_name &&
                              oneShop.first_name.toLowerCase().includes(query); // Pokud chcete hledat i v oneShop.first_name
                            const shopLastNameMatch =
                              oneShop.last_name &&
                              oneShop.last_name.toLowerCase().includes(query); // Pokud chcete hledat i v oneShop.last_name
                            const shopIcoMatch =
                              oneShop.ico &&
                              oneShop.ico.toLowerCase().includes(query);
                            const shopEmailMatch =
                              oneShop.email &&
                              oneShop.email.toLowerCase().includes(query); // Volitelně: Hledat i v top-level emailu?

                            // 2. Zkontrolujte emaily v poli users (jak to máte teď)
                            const usersEmailMatch =
                              oneShop.users &&
                              oneShop.users.some(
                                (user) =>
                                  user.email &&
                                  user.email.toLowerCase().includes(query)
                              );

                            // 3. Spojte všechny kontroly: Obchod se zobrazí, pokud se shoduje ALESPOŇ JEDNA z kontrol
                            const isMatch =
                              website.includes(query) || // Shoda webu
                              shopNameMatch || // Shoda jména obchodu (nebo kontaktu)
                              shopFirstNameMatch || // Shoda křestního jména (pokud relevantní)
                              shopLastNameMatch || // Shoda příjmení (pokud relevantní)
                              shopIcoMatch || // Shoda IČO
                              shopEmailMatch || // Shoda top-level emailu (pokud chcete)
                              usersEmailMatch; // Shoda emailu v poli users

                            // Pokud nebyla nalezena ŽÁDNÁ shoda, obchod odfiltrujte
                            if (!isMatch) {
                              return false;
                            }
                          }
                        }

                        // Pokud vyhledávací dotaz není zadán, nebo byla nalezena shoda,
                        // pokračuje se k filtrování podle statusu.
                        const baseStatus = getShopStatus(oneShop);
                        const shopEndDate = oneShop.end_date
                          ? new Date(oneShop.end_date)
                          : null;
                        const status = shopEndDate ? "Ukončeno" : baseStatus;
                        return selectedStatuses[status];
                      })
                      .sort((a, b) => {
                        const getDisplayStatus = (shop) => {
                          const baseStatus = getShopStatus(shop);
                          const shopEndDate = shop.end_date
                            ? new Date(shop.end_date)
                            : null;
                          return shopEndDate ? "Ukončeno" : baseStatus;
                        };
                        if (sortOption === "name") {
                          return removeProtocol(a.website).localeCompare(
                            removeProtocol(b.website)
                          );
                        } else if (sortOption === "status") {
                          return getDisplayStatus(a).localeCompare(
                            getDisplayStatus(b)
                          );
                        } else if (sortOption === "start_date") {
                          // Pokud není start_date, řaďte na konec
                          const aDate = a.start_date
                            ? new Date(a.start_date)
                            : null;
                          const bDate = b.start_date
                            ? new Date(b.start_date)
                            : null;
                          if (!aDate && !bDate) return 0;
                          if (!aDate) return 1;
                          if (!bDate) return -1;
                          return aDate - bDate;
                        }
                      })
                      .map((oneShop, index) => {
                        let baseStatus = getShopStatus(oneShop);
                        const now = new Date();
                        const shopEndDate = oneShop.end_date
                          ? new Date(oneShop.end_date)
                          : null;
                        let status = shopEndDate ? "Ukončeno" : baseStatus;

                        let statusBackgroundColor;
                        let statusTextColor;
                        let statusBorder = "none";

                        if (status === "Aktivní") {
                          statusBackgroundColor = "#22E06C";
                        } else if (status === "Pozastaveno") {
                          statusBackgroundColor = "#EFEFEF";
                          statusTextColor = "#555555";
                        } else if (status === "Neaktivní") {
                          statusBackgroundColor = "#999999";
                          statusTextColor = "#ffffff";
                        } else if (status === "Registrace") {
                          statusBackgroundColor = "#fff";
                          statusTextColor = "#000";
                          statusBorder = "2px solid #DAFD82";
                        } else if (status === "Ukončeno") {
                          statusBackgroundColor = "#FFC4C4";
                          statusTextColor = "#000";
                        }

                        return (
                          <li
                            key={oneShop.uuid + index}
                            onClick={() => handleShopSelect(oneShop.uuid)}
                            style={{
                              cursor: "pointer",
                              borderRadius: "4px",
                              margin: "5px 0",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div
                              className="item shop_option_item"
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                alignItems: "center",
                                backgroundColor:
                                  uuid === oneShop.uuid
                                    ? "#e0e0e0"
                                    : "transparent",
                                margin: 0,
                                padding: 0,
                              }}
                            >
                              <span className="item__ico"></span>
                              <span
                                className="item__title"
                                style={{ flexGrow: 1, padding: 0, margin: 0 }}
                              >
                                {removeProtocol(oneShop.website)}
                                {/* Pokud je zvoleno řazení podle start_date, zobrazte start_date vedle website */}
                                {sortOption === "start_date" &&
                                  oneShop.start_date && (
                                    <span
                                      style={{
                                        marginLeft: "10px",
                                        fontSize: "0.9em",
                                        color: "#999",
                                      }}
                                    >
                                      (
                                      {new Date(
                                        oneShop.start_date
                                      ).toLocaleDateString()}
                                      )
                                    </span>
                                  )}
                                {shopEndDate && shopEndDate > now && (
                                  <span
                                    style={{
                                      marginLeft: "10px",
                                      fontSize: "0.9em",
                                      color: "#999",
                                    }}
                                  >
                                    ({shopEndDate.toLocaleDateString()})
                                  </span>
                                )}
                              </span>
                              <span
                                className="item__control"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  margin: "0 !important",
                                  padding: 0,
                                }}
                              >
                                <label
                                  className="switcher"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    margin: "5px 10px",
                                    width: "120px",
                                  }}
                                >
                                  <span
                                    className="switcher__label"
                                    style={{
                                      backgroundColor: statusBackgroundColor,
                                      color: statusTextColor,
                                      padding: "5px 10px",
                                      textAlign: "center",
                                      width: "100%",
                                      borderRadius: "100vh",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      border: statusBorder,
                                    }}
                                  >
                                    {status}
                                  </span>
                                </label>
                              </span>
                            </div>
                          </li>
                        );
                      })}
                </ol>
                {userData?.email === "info@permoniq.com" && (
                  <div className={styles.checkboxContainer}>
                    {[
                      "Aktivní",
                      "Pozastaveno",
                      "Neaktivní",
                      "Registrace",
                      "Ukončeno",
                    ].map((statusOption) => (
                      <label
                        key={statusOption}
                        className={`checkbox ${styles.checkboxLabel}`}
                      >
                        <input
                          type="checkbox"
                          name="checkbox"
                          value={statusOption}
                          checked={selectedStatuses[statusOption]}
                          onChange={handleStatusChange}
                        />
                        {statusOption}
                        <span className="checkbox__checker"></span>
                      </label>
                    ))}
                  </div>
                )}
                {isAdmin && userData.shops.length > 0 && (
                  <span className={styles.shopCount}>{`Počet obchodů: ${
                    userData.shops.filter((oneShop) => {
                      if (searchQuery) {
                        const query = searchQuery.toLowerCase();

                        // Pokud obsahuje pokročilé operátory nebo kombinace, vyhodnoť jako komplexní dotaz
                        if (hasAdvancedQuery(query)) {
                          return evaluateComplexQuery(oneShop, query);
                        }
                        // Pokud začíná jen na 's:' (bez dalších operátorů), filtruj podle statusu
                        else if (query.startsWith("s:")) {
                          const statusValue = query.replace("s:", "").trim();
                          const baseStatus = getShopStatus(oneShop);
                          const shopEndDate = oneShop.end_date
                            ? new Date(oneShop.end_date)
                            : null;
                          const status = shopEndDate ? "Ukončeno" : baseStatus;
                          if (status.toLowerCase() !== statusValue) {
                            return false;
                          }
                        } else {
                          const website = removeProtocol(
                            oneShop.website
                          ).toLowerCase();
                          const shopNameMatch =
                            oneShop.name &&
                            oneShop.name.toLowerCase().includes(query);
                          const shopFirstNameMatch =
                            oneShop.first_name &&
                            oneShop.first_name.toLowerCase().includes(query);
                          const shopLastNameMatch =
                            oneShop.last_name &&
                            oneShop.last_name.toLowerCase().includes(query);
                          const shopIcoMatch =
                            oneShop.ico &&
                            oneShop.ico.toLowerCase().includes(query);
                          const shopEmailMatch =
                            oneShop.email &&
                            oneShop.email.toLowerCase().includes(query);
                          const usersEmailMatch =
                            oneShop.users &&
                            oneShop.users.some(
                              (user) =>
                                user.email &&
                                user.email.toLowerCase().includes(query)
                            );
                          const isMatch =
                            website.includes(query) ||
                            shopNameMatch ||
                            shopFirstNameMatch ||
                            shopLastNameMatch ||
                            shopIcoMatch ||
                            shopEmailMatch ||
                            usersEmailMatch;
                          if (!isMatch) {
                            return false;
                          }
                        }
                      }
                      const baseStatus = getShopStatus(oneShop);
                      const shopEndDate = oneShop.end_date
                        ? new Date(oneShop.end_date)
                        : null;
                      const status = shopEndDate ? "Ukončeno" : baseStatus;
                      return selectedStatuses[status];
                    }).length
                  }`}</span>
                )}
              </div>
            </div>
            {isAdmin ? (
              <div className="btn__wrapper">
                <div className="register__submit">
                  <p>
                    <button className="btn" onClick={() => handleNew()}>
                      Nový
                    </button>
                  </p>
                </div>
                <div className="register__submit">
                  <p>
                    <button className="btn" type="submit">
                      Vybrat
                    </button>
                  </p>
                </div>
              </div>
            ) : (
              <div className="register__submit">
                <p>
                  <button className="btn" type="submit">
                    Vybrat
                  </button>
                </p>
              </div>
            )}
          </form>
        </article>
      </section>
      {userData.email === "info@permoniq.com" && showAdminModal && (
        <Modal
          title="Informace o obchodu"
          cancelText="Zavřít"
          confirmText=""
          onClose={() => setShowAdminModal(false)}
          isDelete={!selectedShop?.end_date}
          onDelete={onTurnOffShopClick}
          extraButtons={
            selectedShop?.end_date ? (
              <button className="btn" onClick={onReactivateShopClick}>
                Obnovit
              </button>
            ) : null
          }
        >
          {infoItems.map((item, index) => (
            <InfoBoxItem key={index} label={item.label} value={item.value} />
          ))}
          <InfoBoxItem
            label="Stav kampaně"
            value={
              selectedShop?.end_date
                ? "Ukončeno"
                : getShopStatus(selectedShop)
            }
          />
        </Modal>
      )}
      {showDeleteConfirmation && (
        <Modal
          title={"Vypnutí obchodu"}
          cancelText="Zpět"
          confirmText=""
          isDelete={true}
          onDelete={() => {
            handleTurnOffShop();
            setShowDeleteConfirmation(false);
          }}
          onClose={() => {
            setShowDeleteConfirmation(false);
            setShowAdminModal(true);
          }}
        >
          <p>{`Opravdu chcete vypnout obchod ${selectedShop?.name}?`}</p>
          <div className={styles.deletePassBox}></div>
        </Modal>
      )}
      {showReactivateConfirmation && (
        <Modal
          title={"Obnovení obchodu"}
          cancelText="Zpět"
          confirmText="Obnovit"
          onConfirm={() => {
            handleReactivateShop();
            setShowReactivateConfirmation(false);
          }}
          onClose={() => {
            setShowReactivateConfirmation(false);
            setShowAdminModal(true);
          }}
        >
          <p>{`Opravdu chcete obnovit obchod ${selectedShop?.name}?`}</p>
          {hasPausedCampaign(selectedShop) && (
            <label
              className="checkbox"
              style={{ display: "block", marginTop: "10px" }}
            >
              <input
                type="checkbox"
                name="reactivateCampaign"
                checked={reactivateCampaignToo}
                onChange={(e) => setReactivateCampaignToo(e.target.checked)}
              />
              Znovu spustit pozastavenou kampaň
              <span className="checkbox__checker"></span>
            </label>
          )}
        </Modal>
      )}
    </>
  );
};

export default ShopOption;
